/**
 * SankeyDiagramControl
 *
 * Sankey diagram for flow visualization
 * Shows source -> target flows with magnitude
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

interface Flow {
  source: string;
  target: string;
  value: number;
}

const SankeyDiagramControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const sankeyConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      sourceKey: (config?.sourceKey as string) || 'source',
      targetKey: (config?.targetKey as string) || 'target',
      valueKey: (config?.valueKey as string) || 'value',
      nodeWidth: (config?.nodeWidth as number) || 40,
    };
  }, [control.config]);

  const renderSankey = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract flows
    const flows: Flow[] = [];
    const nodes = new Set<string>();

    chartData.forEach((d) => {
      const source = String((d as Record<string, unknown>)[sankeyConfig.sourceKey] || '');
      const target = String((d as Record<string, unknown>)[sankeyConfig.targetKey] || '');
      const value = (d as Record<string, unknown>)[sankeyConfig.valueKey] as number | undefined;

      if (source && target && typeof value === 'number' && value > 0) {
        flows.push({ source, target, value });
        nodes.add(source);
        nodes.add(target);
      }
    });

    if (flows.length === 0) return null;

    const nodeArray = Array.from(nodes);
    const totalValue = flows.reduce((a, b) => a + b.value, 0);

    // Simple layout: sources on left, targets on right
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const padding = 40;

    const sources = new Set(flows.map((f) => f.source));
    const targets = new Set(flows.map((f) => f.target));

    // Position nodes
    const nodePositions: Record<string, { x: number; y: number; height: number }> = {};
    let sourceY = padding;
    Array.from(sources).forEach((node) => {
      nodePositions[node] = { x: padding, y: sourceY, height: 30 };
      sourceY += 50;
    });

    let targetY = padding;
    Array.from(targets).forEach((node) => {
      if (!nodePositions[node]) {
        nodePositions[node] = { x: width - padding - sankeyConfig.nodeWidth, y: targetY, height: 30 };
        targetY += 50;
      }
    });

    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c',
      '#d084d0', '#13c2c2', '#f5ad14', '#eb2f96', '#52c41a',
    ];

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Flows (paths) */}
        {flows.map((flow, idx) => {
          const sourcePos = nodePositions[flow.source];
          const targetPos = nodePositions[flow.target];

          if (!sourcePos || !targetPos) return null;

          const flowHeight = (flow.value / totalValue) * (height - 2 * padding);
          const opacity = Math.min(0.3 + (flow.value / totalValue) * 0.5, 0.7);

          const x1 = sourcePos.x + sankeyConfig.nodeWidth;
          const y1 = sourcePos.y + sourcePos.height / 2;
          const x2 = targetPos.x;
          const y2 = targetPos.y + targetPos.height / 2;

          const color = colors[idx % colors.length];

          return (
            <g key={`flow-${idx}`}>
              <path
                d={`M ${x1} ${y1} C ${(x1 + x2) / 2} ${y1} ${(x1 + x2) / 2} ${y2} ${x2} ${y2}`}
                fill="none"
                stroke={color}
                strokeWidth={Math.max(flowHeight, 2)}
                opacity={opacity}
              />
            </g>
          );
        })}

        {/* Nodes */}
        {Object.entries(nodePositions).map(([nodeName, pos], idx) => {
          return (
            <g key={`node-${idx}`}>
              <rect
                x={pos.x}
                y={pos.y}
                width={sankeyConfig.nodeWidth}
                height={pos.height}
                fill={colors[idx % colors.length]}
                opacity="0.9"
                stroke="#000"
                strokeWidth="1"
              />
              <text
                x={pos.x + sankeyConfig.nodeWidth / 2}
                y={pos.y + pos.height / 2 + 5}
                textAnchor="middle"
                fontSize="11"
                fill="white"
                fontWeight="bold"
              >
                {nodeName.substring(0, 8)}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, sankeyConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="sankey-diagram">
      <div style={{ width: '100%', height: '100%' }}>{renderSankey}</div>
    </BaseChartComponent>
  );
});

SankeyDiagramControl.displayName = 'SankeyDiagramControl';

export default SankeyDiagramControl;
